import {action, computed, observable} from 'mobx';
import {ITreeNode} from '@techsee/techsee-ui-common/lib/simple-tree-control-view';
import {IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {EventLogService} from '../../services/event-log/event-log.service';
import {EventEmitter} from 'events';
import isEmpty from 'lodash/isEmpty';
import {uuidv4} from '@techsee/techsee-common/lib/core/uuid';
import {ITreeController} from './contract';
import {DeepReadonly} from '@techsee/techsee-common/lib/core/data-structures';

export abstract class BaseTreeController<TEvents extends string = string> implements ITreeController {
    @observable.deep protected _treeNode: ITreeNode = {
        id: uuidv4(),
        parents: [],
        children: [],
        state: {expanded: true, selected: false, disabled: false},
        data: {title: 'root', type: 'dir'}
    };

    protected eventEmitter: EventEmitter;

    @computed get treeData(): DeepReadonly<ITreeNode> {
        return this._treeNode;
    }

    @computed get selectedNode(): ITreeNode | null {
        return this.findSelectedNode(this._treeNode);
    }

    protected findSelectedNode = (node: ITreeNode): ITreeNode | null => {
        if (node.state?.selected) {
            return node;
        }

        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                const found = this.findSelectedNode(child);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    on(event: TEvents, listener: (...args: any[]) => void): void {
        this.eventEmitter.on(event as string, listener);
    }

    constructor(
        protected accountId: string,
        protected userId: string,
        protected owners: string,
        protected resourceService: IResourcesService,
        protected eventLog: EventLogService,
        protected trace: any,
        public useNewInterface: boolean
    ) {
        this.eventEmitter = new EventEmitter();
    }

    off(event: TEvents, listener: (...args: any[]) => void): void {
        this.eventEmitter.off(event as string, listener);
    }

    dispose(): void {
        this.eventEmitter.removeAllListeners();
    }

    protected emit(event: TEvents, ...args: any[]): void {
        this.eventEmitter.emit(event as string, ...args);
    }

    @action
    expandCollapse = (node: ITreeNode): void => {
        if (node && node.state) {
            node.state.expanded = !node.state.expanded;
        }
    };

    @computed
    get empty(): boolean {
        return isEmpty(this.treeData.parents) && isEmpty(this.treeData.children);
    }

    abstract selectNode(node: ITreeNode): void;
}
